import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Showreel from "@/components/Showreel";
import SEO from "@/components/SEO";
import { Play, Film as FilmIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudioPortfolio = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);

  const categories = [
    { id: "all", label: "All" },
    { id: "Commercials", label: "Commercials" },
    { id: "Brand Films", label: "Brand Films" },
    { id: "Social Content", label: "Social Content" },
    { id: "Motion", label: "Motion & Animation" },
    { id: "BTS", label: "BTS / Studio Work" }
  ];

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      setPortfolio(response.data);
    } catch (error) {
      console.error("Error fetching portfolio:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPortfolio = selectedCategory === "all"
    ? portfolio
    : portfolio.filter(item => item.project_type === selectedCategory);

  return (
    <div className="min-h-screen bg-[#030303] studio-body">
      <SEO
        brand="studio"
        path="/studio/portfolio"
        title="Portfolio"
        description="Brand films, commercials, motion, and cinematic social content from Wake N' Bake Studio."
      />
      <Navigation brand="studio" />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6 film-grain">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="portfolio-heading">
              OUR
              <span className="text-[#F97316]"> WORK</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A showcase of brand stories, cinematic films, and visual experiences we've crafted for clients worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Dedicated Showreel Section */}
      <section className="px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <Showreel
            variant="section"
            eyebrow="2026 SHOWREEL"
            title="A YEAR IN FRAMES"
          />
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-6 bg-[#0A0A0A] sticky top-20 z-40">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 font-medium transition-all ${
                  selectedCategory === category.id
                    ? "bg-[#F97316] text-white"
                    : "bg-[#0A0A0A] border border-[#262626] text-gray-400 hover:border-[#F97316]/50 hover:text-white"
                }`}
                data-testid={`category-filter-${category.id}`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredPortfolio.length === 0 ? (
            <div className="text-center py-20" data-testid="no-portfolio-items">
              <FilmIcon className="w-20 h-20 text-gray-600 mx-auto mb-6" />
              <h3 className="text-2xl text-gray-400 mb-4">No portfolio items yet</h3>
              <p className="text-gray-500">Check back soon for inspiring visual work.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPortfolio.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`portfolio-item-${index}`}
                >
                  <div className="relative overflow-hidden bg-[#0A0A0A] aspect-video">
                    {item.thumbnail_url || item.image_url ? (
                      <img
                        src={item.thumbnail_url || item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#0A0A0A]">
                        <FilmIcon className="w-16 h-16 text-gray-700" />
                      </div>
                    )}
                    {item.video_url && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="w-16 h-16 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="text-sm text-[#F97316] mb-2">{item.project_type}</div>
                    <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#F97316] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-400">{item.client}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      {selectedItem && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedItem(null)}
          data-testid="portfolio-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="bg-[#0A0A0A] rounded-none max-w-5xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <button
                onClick={() => setSelectedItem(null)}
                className="float-right text-gray-400 hover:text-white text-2xl"
                data-testid="close-portfolio-modal"
              >
                ×
              </button>

              <div className="text-sm text-[#F97316] mb-2">{selectedItem.project_type}</div>
              <h2 className="heading-font text-4xl text-white mb-4">{selectedItem.title}</h2>
              <p className="text-xl text-gray-400 mb-8">{selectedItem.client}</p>

              {selectedItem.video_url && (
                <div className="aspect-video mb-8 bg-black">
                  {selectedItem.video_url.includes('youtube.com') || selectedItem.video_url.includes('youtu.be') ? (
                    <iframe
                      src={selectedItem.video_url.replace('watch?v=', 'embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      title={selectedItem.title}
                    />
                  ) : selectedItem.video_url.includes('vimeo.com') ? (
                    <iframe
                      src={selectedItem.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                      className="w-full h-full"
                      allowFullScreen
                      title={selectedItem.title}
                    />
                  ) : (
                    <video controls className="w-full h-full">
                      <source src={selectedItem.video_url} />
                    </video>
                  )}
                </div>
              )}

              {selectedItem.image_url && !selectedItem.video_url && (
                <img
                  src={selectedItem.image_url}
                  alt={selectedItem.title}
                  className="w-full h-auto mb-8"
                />
              )}

              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-white">Project Details</h3>
                <p className="text-gray-300">{selectedItem.description}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      <Footer brand="studio" />
    </div>
  );
};

export default StudioPortfolio;